import { useState, useEffect, useRef, useCallback } from 'react'

// 通用API调用Hook
// params:
//   apiCall: 请求函数
//   dependencies: 依赖数组，变化时会触发重新请求
export const useAPI = (apiCall, dependencies = []) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const mountedRef = useRef(true)
  
  // 保持最新的 apiCall 引用
  const apiCallRef = useRef(apiCall)
  apiCallRef.current = apiCall

  const fetchData = useCallback(async () => {
    const currentApiCall = apiCallRef.current

    try {
      if (mountedRef.current) {
        setLoading(true)
        setError(null)
      }

      const result = await currentApiCall()
      
      if (mountedRef.current) {
        setData(result)
      }
      return result
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message || '请求失败')
      }
      throw err
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [...dependencies])

  useEffect(() => {
    mountedRef.current = true
    fetchData()
    
    return () => {
      mountedRef.current = false
    }
  }, [fetchData])

  const refetch = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await fetchData()
      return result
    } catch (err) {
      setError(err.message || '请求失败')
      throw err
    } finally {
      setLoading(false)
    }
  }, [fetchData])

  return { data, loading, error, refetch }
}

// 懒加载API调用Hook
export const useLazyAPI = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const execute = async (apiCall) => {
    try {
      setLoading(true)
      setError(null)
      const result = await apiCall()
      setData(result)

      return result
    } catch (err) {
      setError(err.message || '请求失败')
      throw err
    } finally {
      setLoading(false)
    }
  }

  return { data, loading, error, execute }
}

export default useAPI 